import { Dropzone, IconImage, ModalV2 } from "@/components";
import { excel_img } from "@/constants";
import useSnackbar from "@/hooks/useSnackbar.ts";
import useUploadFile from "@/hooks/useUploadFile.ts";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { FC, ReactElement, useCallback, useState } from "react";
import {
  ProgressMain,
  UploadActions,
  UploadContainerMain,
  UploadDetails,
  UploadProgressContainer,
  UploadProgressText,
  UploadSize,
} from "./styles.tsx";
import { formatFileSize } from "@/utils/helpers.ts";
interface DataModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  subTitle: string;
  buttonAction: (ele: DropzoneTypes[]) => void;
  buttonText: string;
  btnLoader: boolean;
  setTimeStamp: React.Dispatch<React.SetStateAction<string>>;
}


const UploadData: FC<DataModalProps> = ({
  setShow,
  show,
  title,
  subTitle,
  buttonText,
  buttonAction,
  btnLoader = false,
  setTimeStamp,
}): ReactElement => {
  const theme = useTheme();
  const { show: toast } = useSnackbar();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.only("xs"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.only("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.only("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.only("lg"));
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.only("xl"));
  const [uploads, setUploads] = useState<DropzoneTypes[]>([]);
  const { uploadFileTradeData } = useUploadFile();
  const [hasFile, setHasFile] = useState(false);



  const confirmAddDocument = () => {
    if (uploads.length === 0) return toast("Please Add File", "", "error");
    // Don't set timestamp here - it's already set when file was uploaded
    buttonAction(uploads);
  };

  const modalStyles = {
    zoom: isExtraSmallScreen
      ? 0.7
      : isSmallScreen
      ? 0.7
      : isMediumScreen
      ? 0.8
      : isLargeScreen
      ? 0.8
      : isExtraLargeScreen
      ? 1
      : 1,
  };

  const uploadDocument = useCallback(
    async (file: File) => {
      if (uploads.length > 0) return toast("Only one file allowed", "", "error");
      setHasFile(true);
      setUploads([
        {
          name: file?.name,
          size: file?.size,
          progress: 0,
          fileUrl: "",
        },
      ]);
      
      // Generate timestamp at upload time
      const uploadTimeStamp = new Date().toISOString();
      
      const data = await uploadFileTradeData(
        file,
        "1",
        uploadTimeStamp
      );
      const { key, timeStamp: actualTimeStamp } = data ?? {};

      if (!key) return;

      // Update the parent component's timestamp with the actual one used
      const finalTimeStamp = actualTimeStamp || uploadTimeStamp;
      setTimeStamp(finalTimeStamp);

      setUploads([{
        name: file?.name,
        size: file?.size,
        progress: 100,
        fileUrl: key,
      }]);
      setHasFile(false);
      toast("File successfully uploaded", "", "success");
    },
    [uploadFileTradeData, uploads.length, setTimeStamp]
  );

  const handleDeleteFile = () => {
    setUploads([]);
  };
  return (
    <ModalV2
      setShow={setShow}
      show={show}
      title={title}
      subTitle={subTitle}
      buttonText={buttonText}
      buttonAction={confirmAddDocument}
      modalStyles={modalStyles}
      isConfirmButtonLoading={btnLoader}
      buttonLoader={hasFile || uploads.length === 0}
    >
      <Box>
        <Typography
          sx={{
            marginTop: "10px",
            color: "#030712",
          }}
          variant="body2"
        >
          Upload Trade Data File
        </Typography>
        <Dropzone
          // maxSize={2 * 1024 * 1024} // 2 MB
          onUpload={uploadDocument}
          extensions={["xlsb", "xlsx", "csv"]}
          fileFormat={"xlsb, xlsx, or csv"}
        />
        {uploads.map((ele: DropzoneTypes, index: number) => (
          <UploadContainerMain container alignItems="center" key={index}>
            <Grid item xs={7}>
              <UploadDetails>
                <Box>
                  <IconImage src={excel_img} size="40px" />
                </Box>
                <Box>
                  <Typography variant="body1">{ele.name}</Typography>
                  <UploadSize variant="body2">
                    {" "}
                    {formatFileSize(ele.size)}
                  </UploadSize>
                </Box>
              </UploadDetails>
            </Grid>

            <UploadActions item xs={5}>
              <IconButton
                onClick={() => handleDeleteFile()}
                style={{ padding: "0", margin: "0px 5px 5px 5px" }}
                size="small"
              >
                <CloseIcon sx={{ color: "#8E9494" }} />
              </IconButton>
            </UploadActions>

            <ProgressMain>
              <Grid item xs={10.8}>
                <UploadProgressContainer>
                  <LinearProgress
                    variant="determinate"
                    value={ele.progress}
                    style={{ width: "100%" }}
                  />
                  <UploadProgressText variant="body2">
                    {`${ele.progress}%`}
                  </UploadProgressText>
                </UploadProgressContainer>
              </Grid>
            </ProgressMain>
          </UploadContainerMain>
        ))}
      </Box>
    </ModalV2>
  );
};

export default UploadData;
